import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Eye, EyeOff, Link2, Shield, ArrowLeft } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface Platform {
  id: string;
  name: string;
  description: string;
  color: string;
  icon: string;
}

interface Credential {
  id: string;
  platform: string;
  email: string;
  created_at: string;
  updated_at: string;
  last_used_at: string | null;
}

const platforms: Platform[] = [
  { id: 'indeed', name: 'Indeed', description: "World's #1 job site", color: 'blue', icon: '🔵' },
  { id: 'linkedin', name: 'LinkedIn', description: 'Professional networking', color: 'blue', icon: '💼' },
  { id: 'ziprecruiter', name: 'ZipRecruiter', description: 'Smart job matching', color: 'green', icon: '🟢' },
  { id: 'glassdoor', name: 'Glassdoor', description: 'Jobs & company reviews', color: 'green', icon: '🟩' },
  { id: 'greenhouse', name: 'Greenhouse', description: 'ATS for tech companies', color: 'green', icon: '🌱' },
  { id: 'lever', name: 'Lever', description: 'Modern ATS platform', color: 'purple', icon: '⚡' }
];

export default function ConnectedAccounts() {
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState<Platform | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchCredentials();
  }, []);

  const fetchCredentials = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({ title: 'Error', description: 'Not authenticated', variant: 'destructive' });
        return;
      }

      const response = await supabase.functions.invoke('get-user-credentials');
      
      if (response.error) throw response.error;
      
      setCredentials(response.data?.credentials || []);
    } catch (error) {
      console.error('Error fetching credentials:', error);
      toast({ title: 'Error', description: 'Failed to load connected accounts', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = (platform: Platform) => {
    const existing = credentials.find(c => c.platform === platform.id);
    setSelectedPlatform(platform);
    setFormData({ email: existing?.email || '', password: '' });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!selectedPlatform) return;
    
    if (!formData.email || !formData.password) {
      toast({ title: 'Error', description: 'Please fill in all fields', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({ title: 'Error', description: 'Not authenticated', variant: 'destructive' });
        return;
      }

      const response = await supabase.functions.invoke('save-user-credentials', {
        body: {
          platform: selectedPlatform.id,
          email: formData.email,
          password: formData.password
        }
      });

      if (response.error) throw response.error;

      toast({ title: 'Success', description: `${selectedPlatform.name} account connected` });
      setIsDialogOpen(false);
      setFormData({ email: '', password: '' });
      fetchCredentials();
    } catch (error: any) {
      console.error('Error saving credentials:', error);
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to save credentials', 
        variant: 'destructive' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async (platform: Platform) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({ title: 'Error', description: 'Not authenticated', variant: 'destructive' });
        return;
      }

      const response = await supabase.functions.invoke('delete-user-credentials', {
        body: { platform: platform.id }
      });

      if (response.error) throw response.error;

      toast({ title: 'Success', description: `${platform.name} account disconnected` });
      fetchCredentials();
    } catch (error: any) {
      console.error('Error deleting credentials:', error);
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to disconnect account', 
        variant: 'destructive' 
      });
    }
  };

  const isConnected = (platformId: string) => {
    return credentials.some(c => c.platform === platformId);
  };

  const getCredential = (platformId: string) => {
    return credentials.find(c => c.platform === platformId);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/app/account">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold">Connected Accounts</h1>
              <p className="text-muted-foreground mt-1">
                Connect your job board accounts for seamless auto-apply
              </p>
            </div>
          </div>
        </div>

        <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
          <Shield className="h-4 w-4" />
          <span>Your credentials are encrypted and secure</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-8 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-full" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-10 w-full" />
                </CardContent>
              </Card>
            ))
          ) : (
            platforms.map((platform) => {
              const connected = isConnected(platform.id);
              const credential = getCredential(platform.id);

              return (
                <Card key={platform.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{platform.icon}</span>
                        <div>
                          <CardTitle className="text-lg">{platform.name}</CardTitle>
                          <CardDescription>{platform.description}</CardDescription>
                        </div>
                      </div>
                      {connected ? (
                        <Badge variant="default" className="bg-green-500">Connected</Badge>
                      ) : (
                        <Badge variant="secondary">Not Connected</Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {connected && credential && (
                      <>
                        <div className="text-sm">
                          <span className="text-muted-foreground">Email: </span>
                          <span className="font-medium">{credential.email}</span>
                        </div>
                        {credential.last_used_at && (
                          <div className="text-sm text-muted-foreground">
                            Last used: {new Date(credential.last_used_at).toLocaleDateString()}
                          </div>
                        )}
                      </>
                    )}
                    
                    <div className="flex gap-2">
                      {connected ? (
                        <>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleConnect(platform)}
                          >
                            Update
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="destructive" size="sm">
                                Disconnect
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Disconnect {platform.name}?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will remove your saved credentials for {platform.name}. 
                                  You can reconnect at any time.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDisconnect(platform)}>
                                  Disconnect
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>
                      ) : (
                        <Button 
                          className="w-full"
                          onClick={() => handleConnect(platform)}
                        >
                          <Link2 className="mr-2 h-4 w-4" />
                          Connect Account
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        <Card className="bg-muted/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              How we keep your credentials secure
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span>All passwords are encrypted with AES-256-GCM</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span>We never store plaintext passwords</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span>Credentials only used for auto-apply</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span>You can disconnect anytime</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {isConnected(selectedPlatform?.id || '') ? 'Update' : 'Connect'} {selectedPlatform?.name}
              </DialogTitle>
              <DialogDescription>
                Enter your {selectedPlatform?.name} credentials to enable auto-apply
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-sm">
                <p className="text-amber-700 dark:text-amber-300">
                  ⚠️ Your password is encrypted and only used for applying to jobs. 
                  We never share your credentials.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={8}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Minimum 8 characters
                </p>
              </div>

              <div className="flex gap-2 pt-4">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving} className="flex-1">
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
